import React, { useMemo } from 'react';
import BaseShiftCalendar from './BaseShiftCalendar';
import { useStableArray } from '@/hooks/ui';
import { BaseCalendarProps } from './types';

/**
 * Wrapper around BaseShiftCalendar that stabilizes props to prevent unnecessary re-renders
 * This helps prevent the display hours logic from running infinitely
 */
const OptimizedShiftCalendar = React.memo(React.forwardRef<any, BaseCalendarProps>((props, ref) => {
  const {
    shifts,
    openingHours,
    events,
    employees,
    shiftTemplates,
    dayTemplates,
    ...restProps
  } = props;

  // Stabilize array props that commonly cause re-renders
  const stableShifts = useStableArray(shifts || []);
  const stableOpeningHours = useStableArray(openingHours || []);
  const stableEvents = useStableArray(events || []);
  const stableEmployees = useStableArray(employees || []);
  const stableShiftTemplates = useStableArray(shiftTemplates || []);
  const stableDayTemplates = useStableArray(dayTemplates || []);

  // Memoize the stabilized props object
  const optimizedProps = useMemo(() => ({
    ...restProps,
    shifts: stableShifts,
    openingHours: stableOpeningHours,
    events: stableEvents,
    employees: stableEmployees,
    shiftTemplates: stableShiftTemplates,
    dayTemplates: stableDayTemplates,
  }), [
    restProps,
    stableShifts,
    stableOpeningHours, 
    stableEvents,
    stableEmployees,
    stableShiftTemplates,
    stableDayTemplates
  ]);

  return <BaseShiftCalendar ref={ref} {...optimizedProps} />;
}));

OptimizedShiftCalendar.displayName = 'OptimizedShiftCalendar';

export default OptimizedShiftCalendar;